import React, { useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  ProgressBar,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsExportRequest,
  OmsProduct,
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import useOmsExport from "../../hooks/useOmsExport";
import { comparisonLabel, fileSize } from "./omsReportFormatting";

export default function OMSExportPanel({
  product,
  report,
  mode,
  filters = [],
  operation = "and",
  valid = true,
  onActiveChange,
  onCriteriaLoaded,
}: {
  product: OmsProduct;
  report: OmsReport;
  mode: OmsExportRequest["mode"];
  filters?: OmsRecordFilter[];
  operation?: OmsExportRequest["operation"];
  valid?: boolean;
  onActiveChange?: (active: boolean) => void;
  onCriteriaLoaded?: (
    filters: OmsRecordFilter[],
    operation: "and" | "or"
  ) => void;
}) {
  const state = useOmsExport(product, report.snapshotId, mode);
  const job = state.job.data;
  const full = mode === "full";
  const restoredJob = useRef<string | null>(null);
  const createError = state.create.error as {
    response?: { status?: number; data?: { error?: string; message?: string } };
  } | null;
  useEffect(() => {
    onActiveChange?.(Boolean(state.jobId) || state.create.isLoading);
  }, [state.jobId, state.create.isLoading, onActiveChange]);
  useEffect(() => {
    if (
      job?.mode === "filtered" &&
      onCriteriaLoaded &&
      restoredJob.current !== job.jobId
    ) {
      restoredJob.current = job.jobId;
      onCriteriaLoaded(job.filters, job.operation);
    }
  }, [job, onCriteriaLoaded]);
  return (
    <SpaceBetween size="s">
      {full && (
        <Box color="text-body-secondary">
          Prepare one ZIP containing every CSV file and a report summary. File
          search and dashboard filters do not change this export.
        </Box>
      )}
      {!state.jobId && (
        <Button
          variant="primary"
          iconName="download"
          wrapText={false}
          loading={state.create.isLoading}
          loadingText="Starting export"
          disabled={!valid}
          onClick={() => state.create.mutate({ filters, operation })}
        >
          {full ? "Prepare full report ZIP" : "Prepare filtered report ZIP"}
        </Button>
      )}
      {state.create.isError && (
        <Alert
          type="error"
          header="Could not start export"
          action={<Button onClick={state.reset}>Start new export</Button>}
        >
          {createError?.response?.status === 400
            ? createError.response.data?.error ||
              createError.response.data?.message ||
              "Check the selected columns and criteria, then try again."
            : "Your export did not start. Try preparing it again."}
        </Alert>
      )}
      {state.job.isError && (
        <Alert
          type="error"
          header="Export status unavailable"
          action={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={() => {
                  state.job.refetch().catch(() => undefined);
                }}
              >
                Retry status
              </Button>
              <Button onClick={state.reset}>Start new export</Button>
            </SpaceBetween>
          }
        >
          The request may still be running. Retry to check its status.
        </Alert>
      )}
      {state.jobId && !job && !state.job.isError && (
        <div role="status">Checking export status…</div>
      )}
      {job?.status === "PREPARING" && (
        <ProgressBar
          value={
            job.totalParts ? (job.completedParts / job.totalParts) * 100 : 0
          }
          label="Preparing report archive"
          description={`${job.completedParts.toLocaleString()} of ${job.totalParts.toLocaleString()} source files processed. You can close this dialog and return later.`}
          additionalInfo={
            job.completedParts === job.totalParts
              ? "Assembling the ZIP archive…"
              : "The download becomes available when preparation finishes."
          }
        />
      )}
      {job?.status === "FAILED" && (
        <Alert
          type="error"
          header="Export could not be completed"
          action={<Button onClick={state.reset}>Start new export</Button>}
        >
          {job.error || "Try again or refresh the scheduled report."}
        </Alert>
      )}
      {job && job.mode === "filtered" && (
        <Box color="text-body-secondary">
          Requested criteria (
          {job.operation === "or" ? "match any" : "match all"}
          ):{" "}
          {job.filters
            .map(
              (filter) =>
                `${filter.column} ${comparisonLabel(
                  filter.operator,
                  report.columnTypes?.[filter.column]
                ).toLowerCase()}${
                  filter.value === undefined ? "" : ` ${filter.value}`
                }`
            )
            .join("; ")}
        </Box>
      )}
      {job?.status === "READY" && (
        <SpaceBetween size="s">
          <Alert type="success" header="Report archive ready">
            {job.rowCount?.toLocaleString()} rows ·{" "}
            {job.csvFiles?.toLocaleString()} CSV{" "}
            {job.csvFiles === 1 ? "file" : "files"} ·{" "}
            {fileSize(job.sizeBytes || 0)} ZIP
            {job.rowCount === 0 &&
              (full
                ? ". This report is empty. The CSV contains column headers."
                : ". No rows matched. The CSV contains column headers.")}
          </Alert>
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="primary"
              iconName="download"
              wrapText={false}
              loading={state.download.isLoading}
              loadingText="Preparing archive download"
              onClick={() => state.download.mutate()}
            >
              Download ZIP
            </Button>
            <Button wrapText={false} onClick={state.reset}>
              {full ? "Prepare another archive" : "Change export criteria"}
            </Button>
          </SpaceBetween>
          <Box color="text-body-secondary">
            Available until{" "}
            {new Intl.DateTimeFormat(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(job.expiresAt))}
            .
          </Box>
        </SpaceBetween>
      )}
      {state.download.isError && (
        <Alert type="error" header="Could not start download">
          Choose Download ZIP again to get a fresh download link.
        </Alert>
      )}
      {state.sent && (
        <div role="status" aria-live="polite">
          <Box color="text-body-secondary">
            The archive was sent to your browser. Check your browser’s downloads
            for progress.
          </Box>
        </div>
      )}
    </SpaceBetween>
  );
}
